import { Chip, ChipProps } from '@mui/material';
import tokens from '@verifime/design-tokens';
import { cloneElement, isValidElement, ReactElement } from 'react';
import { StatusColor } from './constants';

/**
 * ColoredChip - A styled chip component with automatic icon color handling.
 *
 * This component automatically enhances icon children with the correct variant and statusColor props,
 * ensuring icons display with contrasting colors against the chip's background.
 *
 * ## Basic Usage
 *
 * ```tsx
 * const color = useStatusColor('success');
 * <ColoredChip
 *   statusColor={color}
 *   label="Verified"
 *   icon={<ColoredIcon iconName="checkCircle" />}
 * />
 * ```
 *
 * ## Automatic Icon Color Injection
 *
 * When you pass a ColoredIcon as the `icon` prop, ColoredChip automatically:
 * 1. Injects `variant="in-chip"` to tell the icon it's inside a colored background
 * 2. Injects `statusColor` so the icon can compute the contrasting fill color
 * 3. The icon automatically uses `statusColor.icon || statusColor.text` (e.g., white on green)
 *
 * This means you DON'T need to manually configure icon colors:
 * ```tsx
 * // ✅ Simple - chip handles everything
 * <ColoredChip statusColor={color}>
 *   <ColoredIcon iconName="checkCircle" />
 * </ColoredChip>
 *
 * // ❌ Old way - no longer needed
 * <ColoredChip statusColor={color}>
 *   <ColoredIcon iconName="checkCircle" sx={{ fill: color?.icon || color?.text }} />
 * </ColoredChip>
 * ```
 *
 * ## Props
 *
 * @param statusColor - StatusColor object from theme hooks for chip background and text colors
 * @param label - The chip label text
 * @param icon - Optional icon element (will be auto-enhanced if it's a ColoredIcon)
 * @param size - Chip size: 'small' | 'medium'
 * @param sx - Additional MUI sx styles
 * @param ...props - All other MUI Chip props (onDelete, onClick, etc.)
 *
 * @see ColoredIcon for the icon component that receives the auto-injected props
 * @see StatusColor type in constants.ts for color object structure
 */
export default function ColoredChip({
  statusColor,
  label,
  sx,
  size = 'small',
  icon,
  ...props
}: { statusColor?: StatusColor } & ChipProps) {
  const color = statusColor;

  let isShowBorder = false;
  if (color) {
    if ('border' in color) {
      isShowBorder = true;
    }
  }

  // Auto-inject variant="in-chip" and statusColor to icon children
  let enhancedIcon = icon;
  if (isValidElement(icon) && statusColor) {
    enhancedIcon = cloneElement(icon as ReactElement<{ variant?: string; statusColor?: StatusColor }>, {
      variant: 'in-chip',
      statusColor,
    });
  }

  return (
    <Chip
      sx={{
        bgcolor: color?.fill,
        color: color?.text,
        padding: `${tokens.spacing3xs} ${tokens.spacing2xs}`,
        // TODO: change to border tokens
        border: isShowBorder && `1px solid ${color?.border}`,
        '& .MuiChip-deleteIcon': { color: color?.text },
        ...sx,
      }}
      label={label}
      size={size}
      icon={enhancedIcon}
      {...props}
    />
  );
}
